import { Alert, Box, IconButton, List, Stack, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import ReplayIcon from "@mui/icons-material/Replay";
import { FC, useEffect } from "react";
import { DetectPattern } from "@/gen/api/securefile";
import { useZSliceUnderContext, FormToken } from '@/utils/zustand-form';
import { CollectEditsMetadata, CollectEditsProtocol } from '@/utils/zustand-form/ext/collectEdits';
import { EditorLineSx } from "./InfoTypeEditor.types";
import DetectPatternLine from "./DetectPatternLine";

const DetectPatternEditor : FC<{
  parentToken?: FormToken,
  baseData: DetectPattern[],
}> = ({baseData, parentToken}) => {
  const utils = useZSliceUnderContext({
    parentToken: parentToken ?? null,
    pathFragment: 'detectPatterns',
    prevData: baseData,
    stageInit: () => ({
      added: [] as DetectPattern[], // sparse array
      removed: new Set<number>(),
    }),
    extraStates: {},
  });
  const { states, setStates, token, installProtocolMetadata } = utils;

  useEffect(() => {
    return installProtocolMetadata<CollectEditsMetadata>(CollectEditsProtocol.metadataKey, {
      triggerValidate: async () => { /* No top-level validation */ },
      overrideCollect: async (node, safeInvokeNode, safeChildrenCollection) => {
        const stage = node.state.stage;
        const prevData = node.state.prevData;
        const ids = [
          ...[...new Array(prevData.length)].map((_, i) => i)
            .filter(i => !stage.removed.has(i))
            .map(i => `r[${i}]`),
          ...stage.added.map((_d: DetectPattern, i: number) => `a[${i}]`).filter((_d: DetectPattern, i: number) => stage.added[i] != null),
        ];
        const childNodes = new Map(
          safeChildrenCollection.values().map((cnode) => [cnode.token.path.split('.').pop() ?? cnode.token.path, cnode])
        );
        const collected = [];
        for (const id of ids) {
          const childNode = childNodes.get(id);
          if (childNode) {
            const result = await safeInvokeNode(childNode);
            collected.push(result.newValue ?? result);
          }
        }
        return collected;
      },
    });
  }, [installProtocolMetadata]);

  useEffect(() => {
    setStates(v => ({...v, prevData: baseData, stage: {added: [], removed: new Set()}, touched: {}}))
  }, [baseData, setStates])

  const actionBtn = (d: DetectPattern, i: number) => {
    const atAdded = states.stage.added.indexOf(d)
    const isRestore = states.stage.removed.has(i)
    const actionFn = () => setStates(v => {
      if (atAdded > -1) { // delete added
        delete v.stage.added[atAdded]
      } else if (isRestore) { // restore removed
        v.stage.removed.delete(i)
      } else { // to remove
        v.stage.removed.add(i)
      }
      return {...v, touched: { ls: Object.keys(v.stage.added).length > 0 || v.stage.removed.size > 0 } as any }
    })

    return (
      <Tooltip title={isRestore ? "Restore" : "Remove"}>
        <IconButton color="secondary" onClick={actionFn}>
          {isRestore ? <ReplayIcon /> : <ClearIcon />}
        </IconButton>
      </Tooltip>)
  }
  const classOf = (d: DetectPattern, i: number) => {
    return states.stage.removed.has(i) ? 'removed' : undefined
  }

  return (
    <Box
      sx={[EditorLineSx as any, {
        '& li.MuiListItem-root div.line-box': {
          flex: '1 1 auto',
          mr: 2,
          '& .MuiTextField-root:last-of-type': {
            flex: '1 1 auto'
          }
        },
      }]}
    >
      <Stack direction="row" alignItems="center" spacing={4} marginBottom={1}>
        <Typography variant="subtitle2">Pattern Detection</Typography>
        <Tooltip title="Add Pattern">
          <IconButton color="primary"
            onClick={() => setStates(v => {
              v.stage.added.push({ name: '', score: 0.05, regex: ''})
              return {...v, touched: { ls: true } as any }
            })}
          >
            <AddIcon /></IconButton>
        </Tooltip>
      </Stack>
      <Alert severity="info" sx={{ mb: 1 }}>
        Each pattern match is designated with a matching score. If a piece of content matches multiple patterns, the highest score out of all matches is used.
      </Alert>
      <List disablePadding>
        {states.stage.added.map((d, i) =>(
          <DetectPatternLine parentToken={token} className="added" key={i} r_key={`a[${i}]`} prevData={d} actionButton={actionBtn(d, i)}/>
        )).filter(d => !!d).reverse()}
      </List>
      <List disablePadding>
        {states.prevData.map((d, i) => (
          <DetectPatternLine parentToken={token} className={classOf(d, i)} key={i} r_key={`r[${i}]`} prevData={d} actionButton={actionBtn(d, i)}/>
        ))}
      </List>
    </Box>
  )
}

export default DetectPatternEditor;
